import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, ArrowUpDown, AlertCircle, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SuperHeader, { WA_LABEL, apiErr } from "@/components/superadmin/SuperHeader";
import StatCards from "@/components/superadmin/StatCards";
import StatusAction from "@/components/superadmin/StatusAction";
import Pager from "@/components/superadmin/Pager";

const DEFAULTS = { q: "", status: "all", wa: "all", kind: "all", sort: "created_at", order: "desc", page: "1" };

function Filters({ p, set }) {
  const [q, setQ] = useState(p.q);
  useEffect(() => setQ(p.q), [p.q]);
  useEffect(() => { const t = setTimeout(() => q !== p.q && set({ q, page: "1" }), 350); return () => clearTimeout(t); }, [q]); // eslint-disable-line
  const sel = (key, opts, testid, w = "w-40") => (
    <Select value={p[key]} onValueChange={(v) => set({ [key]: v, page: "1" })}>
      <SelectTrigger className={w} data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent className="bg-white">{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Company, admin name or email…" className="pl-8 w-64" data-testid="super-search" />
      </div>
      {sel("status", [["all", "All statuses"], ["active", "Active"], ["suspended", "Suspended"]], "filter-status")}
      {sel("wa", [["all", "Any WhatsApp"], ["live_verified", "Live · Verified"], ["live_issue", "Live · Error or not tested"], ["live_error", "Live · Error"], ["live_unverified", "Live · Not tested"], ["mock", "Mock (demo)"], ["none", "Not connected"]], "filter-wa", "w-44")}
      {sel("kind", [["all", "All tenants"], ["real", "Real tenants"], ["test", "Test / demo"]], "filter-kind")}
      {sel("sort", [["created_at", "Registration date"], ["name", "Name"], ["user_count", "Users"], ["contact_count", "Contacts"], ["lead_count", "Leads"]], "filter-sort", "w-44")}
      <Button variant="outline" size="icon" onClick={() => set({ order: p.order === "desc" ? "asc" : "desc" })} title={`Order: ${p.order}`} data-testid="toggle-order">
        <ArrowUpDown className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => set({ ...DEFAULTS })} data-testid="reset-filters">Reset</Button>
    </div>
  );
}

function CompanyRow({ c, onOpen, onChanged }) {
  const wa = WA_LABEL[c.wa_state] || WA_LABEL.none;
  return (
    <tr className="border-t border-slate-100 hover:bg-indigo-50/40 cursor-pointer transition-colors" onClick={onOpen} data-testid={`co-row-${c.id}`}>
      <td className="p-3">
        <button className="font-medium text-indigo-700 hover:underline text-left" onClick={(e) => { e.stopPropagation(); onOpen(); }} data-testid={`co-name-${c.id}`}>{c.name}</button>
        <div className="text-[11px] text-slate-500 flex gap-1 items-center">{new Date(c.created_at).toLocaleDateString()}{c.is_test && <Badge className="bg-amber-50 text-amber-700 text-[9px] px-1 py-0">TEST</Badge>}</div>
      </td>
      <td className="p-3"><div className="text-sm">{c.admin_name || "—"}</div><div className="text-[11px] text-slate-500">{c.admin_email || ""}</div></td>
      <td className="p-3"><Badge className={wa.cls}>{wa.text}</Badge>{c.wa_phone && <div className="text-[11px] text-slate-500 mt-0.5">{c.wa_phone}</div>}</td>
      <td className="p-3 text-center">{c.user_count}</td>
      <td className="p-3 text-center">{c.contact_count}</td>
      <td className="p-3 text-center">{c.lead_count}</td>
      <td className="p-3">{c.status === "suspended" ? <Badge className="bg-rose-100 text-rose-800">Suspended</Badge> : <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>}</td>
      <td className="p-3 text-right whitespace-nowrap space-x-1">
        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onOpen(); }} data-testid={`view-${c.id}`}>View details</Button>
        <StatusAction company={c} onDone={onChanged} />
      </td>
    </tr>
  );
}

function CompanyTable({ p, set, data, error, reload, onChanged }) {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const open = (c) => nav(`/super-admin/companies/${c.id}`, { state: { from: `/super-admin?${sp.toString()}` } });
  return (
    <Card className="p-4 sm:p-6 border-slate-200" id="companies-table">
      <div className="flex flex-col gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold">Companies</h2>
          <p className="text-xs text-slate-500" data-testid="companies-total">{data ? `${data.total} matching tenant${data.total === 1 ? "" : "s"}` : "Loading…"}</p>
        </div>
        <Filters p={p} set={set} />
      </div>
      {error ? (
        <div className="p-8 text-center text-sm text-rose-700" data-testid="companies-error"><AlertCircle className="w-5 h-5 mx-auto mb-2" />{error}<div><Button size="sm" variant="outline" className="mt-2" onClick={reload}><RefreshCw className="w-3 h-3 mr-1" />Retry</Button></div></div>
      ) : !data ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : data.items.length === 0 ? (
        <div className="p-10 text-center text-sm text-slate-500" data-testid="companies-empty">No companies match these filters.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr><th className="p-3 text-left">Company</th><th className="p-3 text-left">Admin</th><th className="p-3 text-left">WhatsApp</th><th className="p-3 text-center">Users</th><th className="p-3 text-center">Contacts</th><th className="p-3 text-center">Leads</th><th className="p-3 text-left">Status</th><th className="p-3 text-right">Actions</th></tr>
            </thead>
            <tbody>{data.items.map((c) => <CompanyRow key={c.id} c={c} onOpen={() => open(c)} onChanged={onChanged} />)}</tbody>
          </table>
        </div>
      )}
      {data && data.total > 0 && <Pager page={data.page} pages={data.pages} total={data.total} onPage={(n) => set({ page: String(n) })} testid="companies-pager" />}
    </Card>
  );
}

export default function SuperAdmin() {
  const [sp, setSp] = useSearchParams();
  const p = { ...DEFAULTS, ...Object.fromEntries(sp.entries()) };
  const [stats, setStats] = useState(null);
  const [statsErr, setStatsErr] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const tableRef = useRef(null);

  const set = (patch) => {
    const next = { ...p, ...patch };
    setSp(Object.fromEntries(Object.entries(next).filter(([k, v]) => v !== DEFAULTS[k])));
  };
  const loadStats = () => { setStatsErr(null); api.get("/admin/overview").then((r) => setStats(r.data.stats)).catch((e) => setStatsErr(apiErr(e))); };
  const loadTable = () => {
    setErr(null);
    api.get("/admin/companies", { params: { ...p, page_size: 20 } }).then((r) => setData(r.data)).catch((e) => setErr(apiErr(e)));
  };
  useEffect(loadStats, []);
  useEffect(loadTable, [sp.toString()]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickCard = (to) => {
    setSp(Object.fromEntries(Object.entries({ ...DEFAULTS, order: "desc", ...to }).filter(([k, v]) => v !== DEFAULTS[k])));
    setTimeout(() => tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="super-admin-page">
      <SuperHeader />
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
        {statsErr ? (
          <Card className="p-4 text-sm text-rose-700 border-rose-200" data-testid="stats-error">Could not load statistics: {statsErr} <Button size="sm" variant="outline" onClick={loadStats}>Retry</Button></Card>
        ) : !stats ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">{[...Array(12)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
        ) : <StatCards stats={stats} onSelect={pickCard} />}
        <div ref={tableRef} className="scroll-mt-4">
          <CompanyTable p={p} set={set} data={data} error={err} reload={loadTable} onChanged={() => { loadTable(); loadStats(); }} />
        </div>
      </div>
    </div>
  );
}
